/**
 * @file contrast.ts
 * @description Контраст WCAG 2.x для проверки палитры темы кабинета
 * (phase19.31, db-design §3). Чистые функции без зависимостей.
 *
 * Палитра допускает прозрачность (`#rrggbbaa`: подложка наведения в панели,
 * линии панели). Контраст полупрозрачного цвета не определён сам по себе,
 * поэтому сначала цвет накладывается на свою подложку (обычное альфа-смешение
 * в sRGB — так делает браузер), а потом считается отношение яркостей.
 *
 * Состав палитры модуль не знает: какие ключи участвуют в контрасте, решает
 * реестр пар в validate.ts (декоративный `gradient-warm`, SC66, — не участвует).
 */

/** Цвет в каналах 0–255 и альфа 0–1. */
export interface RgbaColor {
  r: number;
  g: number;
  b: number;
  a: number;
}

const HEX_COLOR = /^#([0-9a-fA-F]{6})([0-9a-fA-F]{2})?$/;

/**
 * Разбор `#rrggbb` / `#rrggbbaa`. `null` — не цвет палитры (короткий hex,
 * `rgb()`, имена цветов сюда не проходят: формат палитры — только hex).
 */
export function parseHexColor(value: string): RgbaColor | null {
  const match = HEX_COLOR.exec(value);
  if (!match) return null;
  const rgb = match[1]!;
  const alpha = match[2];
  return {
    r: Number.parseInt(rgb.slice(0, 2), 16),
    g: Number.parseInt(rgb.slice(2, 4), 16),
    b: Number.parseInt(rgb.slice(4, 6), 16),
    a: alpha === undefined ? 1 : Number.parseInt(alpha, 16) / 255,
  };
}

/** Наложение `top` на `bottom` (source-over). Результат непрозрачен, если `bottom` непрозрачен. */
export function compositeColors(top: RgbaColor, bottom: RgbaColor): RgbaColor {
  const a = top.a + bottom.a * (1 - top.a);
  if (a === 0) return { r: 0, g: 0, b: 0, a: 0 };
  const mix = (t: number, b: number): number => (t * top.a + b * bottom.a * (1 - top.a)) / a;
  return { r: mix(top.r, bottom.r), g: mix(top.g, bottom.g), b: mix(top.b, bottom.b), a };
}

/** Относительная яркость WCAG (каналы 0–255, альфа не учитывается). */
export function relativeLuminance(color: RgbaColor): number {
  const channel = (value: number): number => {
    const c = value / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(color.r) + 0.7152 * channel(color.g) + 0.0722 * channel(color.b);
}

/**
 * Контраст переднего цвета на подложке.
 *
 * Подложка сама может быть полупрозрачной (подложка наведения панели) — тогда
 * она сначала накладывается на `base` (например, саму панель). Без `base`
 * полупрозрачная подложка считается наложенной на белый: это консервативно
 * лишь для светлых схем, поэтому реестр пар всегда передаёт `base` явно.
 */
export function contrastRatio(
  foreground: RgbaColor,
  background: RgbaColor,
  base?: RgbaColor,
): number {
  const white: RgbaColor = { r: 255, g: 255, b: 255, a: 1 };
  let bg = background;
  if (bg.a < 1) bg = compositeColors(bg, base ?? white);
  if (bg.a < 1) bg = compositeColors(bg, white);
  const fg = foreground.a < 1 ? compositeColors(foreground, bg) : foreground;
  const l1 = relativeLuminance(fg);
  const l2 = relativeLuminance(bg);
  const [hi, lo] = l1 >= l2 ? [l1, l2] : [l2, l1];
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Число для людей: «3,7», «4,5», «16,29». Отбрасываем, а не округляем —
 * провал 4,46 не должен выглядеть как «4,5 — нужно не меньше 4,5».
 */
export function formatContrast(value: number): string {
  // Эпсилон — от погрешности двоичной дроби (16.29 * 100 = 1628.999…).
  const truncated = Math.floor(value * 100 + 1e-9) / 100;
  return truncated.toFixed(2).replace(/0+$/, "").replace(/\.$/, "").replace(".", ",");
}
